/**
 * ImageGeneratorNode (Canvas V2 — Sprint 9i migration)
 *
 * Migrated to UnifiedBottomPanel with 3 tabs: Config, Refine, Variants.
 *
 * Changes from pre-migration:
 * - Removed inline config section (renderConfig) — all config lives in Config tab
 * - Removed ImageHistoryPopover — Variants tab replaces it
 * - Removed HistoryBadgeButton — no longer needed
 * - Both standalone and group mode use UnifiedBottomPanel
 * - Card shows only the image display area + toolbar
 * - Loaded asset dimensions drive card width/aspect; config drives placeholders
 */

import { memo, useState, useCallback, useEffect, useMemo } from 'react'
import { createPortal } from 'react-dom'
import { Handle, Position, NodeProps, useEdges, useReactFlow } from 'reactflow'
import { AlertTriangle, BookmarkPlus, MessageSquarePlus } from 'lucide-react'
import { getThumbnailUrl, getAssetUrl, executeNode, cancelNodeExecution, InsufficientSparksError, createNadDebug, type ImageGeneratorNodeData } from '@ponpon/api-client'
import { useClickLock } from '../../../../hooks/useClickLock'
import { useShowInsufficientSparks } from '../../../../contexts/InsufficientSparksContext'
import { useCanvasOptional } from '../../CanvasContext'
import type { ReactFlowCanvasEdgeData, ReactFlowNodeData } from '../../../../hooks/useYCanvas'
import {
  AgentNodeToolbar,
  FloatingNodeLabel,
  NODE_TYPE_ICON,
  UnifiedBottomPanel,
  IMAGE_GEN_TABS,
    DEFAULT_TAB,
  ConfigTabContent,
  RefineTabContent,
  VariantsTabContent,
  useNodeActions,
  useBottomPanelAutoOpen,
  ASPECT_RATIO_MAP,
  ASPECT_WIDTH_MAP,
  snapNaturalDimensionsToDisplayDimensions,
  type SnappedDisplayDimensions,
  type ToolbarAction,
  type SelectedReference,
  buildAssetReferencesFromEdges,
  buildImageReferenceEdgeUpdates,
  createHandlesFromSchema,
} from '../shared'
import type { HistoryEntry } from '../shared/types'
import imageGeneratorSchema from '../../../../../../../schemas/nodes/imageGenerator.json'

// ─── Constants ────────────────────────────────────────────────────────────────

const ASPECT_RATIO_OPTIONS = ['16:9', '9:16', '3:4', '4:3', '1:1']

const HANDLES = createHandlesFromSchema(imageGeneratorSchema, {
  inputDefaults: { position: Position.Left },
  outputDefaults: { position: Position.Bottom },
  inputs: {
    in_upstream: { position: Position.Top },
    in_prompt: { style: { top: '62%' } },
    in_image: { style: { top: '74%' } },
  },
})

const quickInfoLayoutLog = createNadDebug('dw:image-generator:quick-info-layout')

// ─── Data shape ───────────────────────────────────────────────────────────────

import { useProjectId } from '../../../../contexts/ProjectIdContext'
import { buildAssetDownloadFilename } from '../../../../utils/buildDownloadFilename'
import { sanitizeNodeError } from '../../../../utils/sanitizeNodeError'

export type { ImageGeneratorNodeData }

// ─── Component ────────────────────────────────────────────────────────────────

const ImageGeneratorNode = memo((props: NodeProps<ImageGeneratorNodeData>) => {
  const { id, data, selected, dragging } = props
  const {
    cacheKey,
    out_generated_image,
    out_image,
    in_name,
    in_prompt = '',
    in_aspect_ratio = '16:9',
    in_style,
    in_quality = '1K',
    in_model = 'openai-gpt-image-2',
    status = 'idle',
    progressMessage,
    error,
  } = data
  const projectId = useProjectId()

  const { setNodes, getNodes, getEdges } = useReactFlow<ReactFlowNodeData, ReactFlowCanvasEdgeData>()
  const edges = useEdges<ReactFlowCanvasEdgeData>()
  const canvas = useCanvasOptional()
  const { handleDelete, handleDuplicate, handleDownload, handleSaveToLibrary, handleAddToChat } = useNodeActions({ nodeId: id })

  const [assetDisplayDimensions, setAssetDisplayDimensions] = useState<SnappedDisplayDimensions | null>(null)
  const pickedIndex = out_generated_image?.picked_index ?? 0

  // Build image URL from out_generated_image history
  let imagePath: string | undefined
  if (out_generated_image?.history?.length) {
    const idx = out_generated_image.picked_index ?? 0
    imagePath = out_generated_image.history[idx]?.result?.local_path
  }
  if (!imagePath && out_image) {
    imagePath = out_image
  }

  useEffect(() => {
    setAssetDisplayDimensions(null)
  }, [imagePath, pickedIndex])

  const requestedAspectRatio = ASPECT_RATIO_MAP[in_aspect_ratio] ?? '16/9'
  const cssAspectRatio = assetDisplayDimensions?.aspectRatio ?? requestedAspectRatio
  const nodeWidth = assetDisplayDimensions?.width ?? ASPECT_WIDTH_MAP[in_aspect_ratio] ?? 315

  const cacheBust = cacheKey
  const imageUrl =
    imagePath && projectId
      ? getThumbnailUrl(projectId, imagePath, 0, cacheBust)
      : null
  const fullImageUrl =
    imagePath && projectId
      ? getAssetUrl(projectId, imagePath, 0, cacheBust)
      : null

  const isRunning = status === 'running'
  const isPendingExecution = status === 'pending_execution'
  const isPendingCost = status === 'pending_cost_confirmation'
  const showSpinner = isRunning || isPendingExecution || isPendingCost
  const spinClass = isRunning ? 'animate-spin' : isPendingCost ? '' : 'animate-spin-slow'
  const isError = status === 'error'
  const displayError =
    sanitizeNodeError(error) || 'Image generation failed. Review the node settings and try again.'
  const [isCancelling, setIsCancelling] = useState(false)
  const handleCancel = useCallback(() => {
    if (!isRunning || !projectId || isCancelling) return
    setIsCancelling(true)
    cancelNodeExecution(projectId, id)
      .catch((err) => {
        // eslint-disable-next-line no-console
        console.error('cancelNodeExecution failed:', err)
      })
      .finally(() => setIsCancelling(false))
  }, [isRunning, projectId, id, isCancelling])

  // ── History ────────────────────────────────────────────────────────────────

  const historyEntries = useMemo(
    (): HistoryEntry[] => out_generated_image?.history ?? [],
    [out_generated_image?.history]
  )
  const batchPending = isRunning ? (data.batch_pending ?? 0) : 0
  // Model is a server-owned choice — hide it from the user-facing summary.
  const configSummary = `${in_aspect_ratio} · ${in_quality}`

  useEffect(() => {
    if (showSpinner) return
    quickInfoLayoutLog({
      event: 'quick-info-layout:render',
      trace_id: `quick-info-layout:${id}`,
      node_id: id,
      node_type: 'imageGenerator',
      targetId: id,
      targetType: 'quick-info-config-summary-background',
      changed: {
        node_width: nodeWidth,
        aspect_ratio: in_aspect_ratio,
        quality: in_quality,
        history_count: historyEntries.length,
      },
    })
  }, [
    id,
    nodeWidth,
    in_aspect_ratio,
    in_quality,
    historyEntries.length,
    showSpinner,
  ])

  // Variants grid base aspect: latest variant's persisted aspect_ratio when
  // available, otherwise the node's current cssAspectRatio. The variants grid
  // (and its column count) follow this base — a 9:16 latest variant gives a
  // 3-column portrait grid even if the user has since changed the node's
  // in_aspect_ratio. Older variants with a different source aspect get
  // cropped via the inner img/video's `object-cover`, mirroring the main
  // preview's behavior.
  const variantsGridAspect = useMemo(() => {
    const latest = historyEntries[historyEntries.length - 1]
    const stored = latest?.result?.metadata?.aspect_ratio
    return (stored && ASPECT_RATIO_MAP[stored]) || cssAspectRatio
  }, [historyEntries, cssAspectRatio])

  const handleHistorySelect = useCallback(
    (index: number) => {
      const entry = historyEntries[index]
      const localPath = entry?.result?.local_path
      if (!localPath) return

      const newCacheKey = String(Date.now())
      const updatedGenImage = { history: out_generated_image?.history ?? [], picked_index: index }

      setNodes((nds) =>
        nds.map((n) =>
          n.id !== id
            ? n
            : {
                ...n,
                data: {
                  ...n.data,
                  out_generated_image: updatedGenImage,
                  out_image: localPath,
                  cacheKey: newCacheKey,
                },
              }
        )
      )
      canvas?.updateNodeData(id, {
        out_generated_image: updatedGenImage,
        out_image: localPath,
        cacheKey: newCacheKey,
      })
    },
    [id, historyEntries, out_generated_image?.history, setNodes, canvas]
  )

  // ── Bottom panel state ────────────────────────────────────────────────────

  const [bottomPanelOpen, setBottomPanelOpen] = useState(false)
  const [activeTab, setActiveTab] = useState(DEFAULT_TAB)

  // Shared auto-open behavior
  useBottomPanelAutoOpen({
    selected,
    isRunning,
    batchPending,
    setBottomPanelOpen,
    setActiveTab,
  })

  const openPanelToTab = useCallback(
    (tab: string) => {
      setActiveTab(tab)
      setBottomPanelOpen(true)
      setNodes((nodes) => nodes.map((n) => ({ ...n, selected: n.id === id })))
    },
    [id, setNodes]
  )

  // ── Lightbox ───────────────────────────────────────────────────────────────

  const [isModalOpen, setIsModalOpen] = useState(false)

  // ── Config updates ─────────────────────────────────────────────────────────

  const updateConfig = useCallback(
    (key: string, value: string | string[] | undefined) => {
      setNodes((nds) =>
        nds.map((n) => (n.id === id ? { ...n, data: { ...n.data, [key]: value } } : n))
      )
      canvas?.updateNodeData(id, { [key]: value })
    },
    [id, setNodes, canvas]
  )

  // Rename handler for FloatingNodeLabel
  const handleRename = useCallback(
    (newName: string) => {
      updateConfig('in_name', newName)
    },
    [updateConfig],
  )

  // ── Generate handler ───────────────────────────────────────────────────────

  const showInsufficientSparks = useShowInsufficientSparks()
  // Per-node 1s cooldown so a fast double-tap can't fire /execute-node twice.
  const { isLocked, run } = useClickLock(1000)
  const generateLocked = isLocked(id)

  const handleGenerate = useCallback(async (retryCount: number = 1) => {
    // Guard: double-submit or pending_execution/pending_cost_confirmation (orchestrator will fire it)
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation') return

    // Guard: missing projectId (no silent failure)
    if (!projectId) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: 'Missing projectId.' } }
            : n
        )
      )
      return
    }

    void run(id, async () => {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'running', progress: 0, error: undefined } }
            : n
        )
      )

      try {
        await executeNode(
          projectId,
          id,
          undefined,
          retryCount
        )
      } catch (err) {
        // Insufficient sparks goes to the workspace-level modal; revert
        // the optimistic 'running' status to 'idle' so the node card
        // doesn't look stuck.
        if (err instanceof InsufficientSparksError) {
          showInsufficientSparks({ balance: err.balance, required: err.required, nodeId: id })
          setNodes((nds) =>
            nds.map((n) =>
              n.id === id
                ? { ...n, data: { ...n.data, status: 'idle', error: undefined } }
                : n
            )
          )
          return
        }
        setNodes((nds) =>
          nds.map((n) =>
            n.id === id
              ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
              : n
          )
        )
      }
    })
  }, [projectId, id, status, setNodes, run, showInsufficientSparks])

  // ── Download handler ───────────────────────────────────────────────────────

  const onDownload = useCallback(() => {
    if (imagePath) {
      const { filename } = buildAssetDownloadFilename({
        canvasNodes: getNodes(),
        canvasEdges: getEdges(),
        nodeId: id,
        assetPath: imagePath,
        mediaType: 'image',
        variantHistory: out_generated_image?.history,
        pickedIndex: out_generated_image?.picked_index ?? 0,
      })
      handleDownload(imagePath, filename)
    }
  }, [imagePath, handleDownload, getNodes, getEdges, id, out_generated_image])

  // ── Refinement handler ────────────────────────────────────────────────────

  const [feedbackText, setFeedbackText] = useState('')

  const handleRefineSubmit = useCallback(async () => {
    if (status === 'running' || status === 'pending_execution' || status === 'pending_cost_confirmation' || !projectId) return

    setNodes((nds) =>
      nds.map((n) =>
        n.id === id
          ? {
              ...n,
              data: {
                ...n.data,
                status: 'running',
                progressMessage: 'Refining image...',
                error: undefined,
              },
            }
          : n
      )
    )

    try {
      // Write feedback to YDoc before executing
      if (feedbackText.trim()) {
        canvas?.updateNodeData(id, { in_feedback: feedbackText.trim() })
      }
      await executeNode(projectId, id)
      setFeedbackText('')
    } catch (err) {
      setNodes((nds) =>
        nds.map((n) =>
          n.id === id
            ? { ...n, data: { ...n.data, status: 'error', error: String(err) } }
            : n
        )
      )
    }
  }, [projectId, id, feedbackText, status, setNodes, canvas])

  // ── Reference handling for ConfigTabContent ───────────────────────────────

  const currentRefs = useMemo<SelectedReference[]>(() => {
    return buildAssetReferencesFromEdges(getNodes(), edges, id, 'in_images', 'image')
  }, [edges, getNodes, id])

  const handleReferencesChange = useCallback(
    (refs: SelectedReference[]) => {
      if (canvas) {
        canvas.replacePortEdges(id, 'in_images', buildImageReferenceEdgeUpdates(refs))
      }
    },
    [canvas, id]
  )

  // ── Toolbar actions ────────────────────────────────────────────────────────

  const toolbarActions: ToolbarAction[] = useMemo(
    () => [
      {
        key: 'save-to-library',
        label: 'Save to Library',
        icon: <BookmarkPlus className="w-4 h-4" />,
        onClick: handleSaveToLibrary,
        group: true,
      },
      {
        key: 'addToChat',
        label: 'Add to Chat',
        icon: <MessageSquarePlus size={14} />,
        onClick: handleAddToChat,
      },
      {
        key: 'duplicate',
        label: 'Duplicate',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
            />
          </svg>
        ),
        onClick: handleDuplicate,
        group: true,
      },
      {
        key: 'delete',
        label: 'Delete',
        icon: (
          <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
            />
          </svg>
        ),
        onClick: handleDelete,
        variant: 'danger',
      },
    ],
    [handleDuplicate, handleDelete, handleSaveToLibrary, handleAddToChat]
  )

  // ── Panel visibility ──────────────────────────────────────────────────────

  const panelVisible = !!selected && !dragging && bottomPanelOpen 

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <>
      <div className="relative">
        <AgentNodeToolbar actions={toolbarActions} isVisible={!!selected && !dragging} />

        {/* Floating label above the card */}
        <FloatingNodeLabel name={in_name || ''} fallback="Image Generator" typeIcon={NODE_TYPE_ICON.imageGenerator} onRename={handleRename} />

        <div
          className={`rounded-lg overflow-hidden transition-all duration-150 ${showSpinner ? 'border-2 animate-border-glow' : ''}`}
          style={{
            width: nodeWidth,
            boxShadow: selected
              ? '0 0 0 2px rgba(255,255,255,0.7)'
              : isError
                ? '0 0 0 1px rgba(239,68,68,0.5)'
                : showSpinner
                  ? '0 0 0 1px rgba(234,179,8,0.5)'
                  : '0 2px 8px rgba(0,0,0,0.3)',
          }}
        >
          {/* Handles */}
          {HANDLES.map((handle) => (
            <Handle
              key={handle.id}
              type={handle.type}
              position={handle.position}
              id={handle.id}
              className="!bg-transparent !border-0 !w-2 !h-2"
              style={handle.style}
            />
          ))}

          {/* Main content area — all 4 corners rounded */}
          <div className="rounded-lg overflow-hidden">
            {showSpinner ? (
              /* Generating / queued state */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-4"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <div className={`w-6 h-6 border-2 border-[var(--warning)] border-t-transparent rounded-full ${spinClass}`} />
                <span className="text-xxs text-foreground/50 mt-2 text-center leading-relaxed">
                  {isRunning
                    ? (progressMessage || 'Image Generator is creating your image now')
                    : isPendingCost
                      ? 'Waiting for cost approval in chat...'
                      : 'Queued — waiting for upstream nodes...'}
                </span>
                {isRunning && (
                  <span className="text-xxs text-foreground/25 mt-1">
                    This may take up to 20 seconds
                  </span>
                )}
                {isRunning && (
                  <button
                    onClick={(e) => { e.stopPropagation(); handleCancel() }}
                    disabled={isCancelling}
                    className="mt-3 flex items-center gap-1.5 px-3 py-1 rounded-full border border-white/20 bg-white/10 hover:bg-white/20 text-white/70 hover:text-white transition-colors nodrag"
                    data-testid="cancel-node-button"
                    title="Stop"
                  >
                    <svg className="w-2.5 h-2.5" viewBox="0 0 24 24" fill="currentColor">
                      <rect x="6" y="6" width="12" height="12" rx="1" />
                    </svg>
                    <span className="text-xxs font-medium">Stop</span>
                  </button>
                )}
              </div>
            ) : imageUrl ? (
              /* Image state — with optional error overlay */
              <div
                className="relative group"
                style={{ aspectRatio: cssAspectRatio, overflow: 'hidden' }}
              >
                <img
                  src={imageUrl}
                  alt="Generated"
                  className={`w-full h-full object-cover block transition-all duration-300 ease-out ${
                    isError
                      ? 'brightness-50'
                      : !selected ? 'group-hover:scale-[1.02]' : ''
                  }`}
                  onLoad={(event) => {
                    setAssetDisplayDimensions(
                      snapNaturalDimensionsToDisplayDimensions(
                        event.currentTarget.naturalWidth,
                        event.currentTarget.naturalHeight,
                      ),
                    )
                  }}
                />

                {/* Error overlay on top of dimmed image */}
                {isError && (
                  <div className="absolute inset-0 flex flex-col items-center justify-center px-6">
                    <AlertTriangle className="w-8 h-8 text-destructive/90" strokeWidth={1.5} />
                    <span
                      className="text-xxs text-destructive mt-1.5 text-center line-clamp-2 cursor-help drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]"
                      title={displayError}
                    >
                      {displayError}
                    </span>
                    <span className="text-xxs text-foreground/50 mt-1 drop-shadow-[0_1px_2px_rgba(0,0,0,0.8)]">Review settings and retry</span>
                  </div>
                )}

                {/* Overlay controls (hidden during error) */}
                {!isError && (
                  <div
                    className="absolute top-2 right-2 flex gap-1 z-10 opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      setIsModalOpen(true)
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Expand"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 8V4m0 0h4M20 8V4m0 0h-4M4 16v4m0 0h4M20 16v4m0 0h-4"
                      />
                    </svg>
                  </button>
                  <button
                    onClick={(e) => {
                      e.stopPropagation()
                      onDownload()
                    }}
                    className="w-7 h-7 rounded bg-black/60 hover:bg-black/80 text-foreground flex items-center justify-center backdrop-blur-sm transition-colors"
                    title="Download"
                  >
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"
                      />
                    </svg>
                  </button>
                </div>
                )}
              </div>
            ) : isError ? (
              /* Error state (no previous image) */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center px-6"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <AlertTriangle className="w-8 h-8 text-destructive/70" strokeWidth={1.5} />
                <span
                  className="text-xxs text-destructive/90 mt-1.5 text-center line-clamp-2 cursor-help"
                  title={displayError}
                >
                  {displayError}
                </span>
                <span className="text-xxs text-foreground/20 mt-1">Review settings and retry</span>
              </div>
            ) : (
              /* Empty placeholder */
              <div
                className="w-full bg-canvas flex flex-col items-center justify-center"
                style={{ aspectRatio: cssAspectRatio }}
              >
                <svg
                  className="w-8 h-8 text-foreground/10"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={1}
                    d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                  />
                </svg>
                <span className="text-xxs text-foreground/20 mt-1">Image Generator</span>
              </div>
            )}
          </div>

          {/* Error footer (visible whenever error, even with previous image) */}
          {isError && !showSpinner && (
            <div className="px-3 py-2 bg-destructive/10 border-t border-destructive/30">
              <span className="text-xxs text-destructive line-clamp-2 cursor-help" title={displayError}>
                {displayError}
              </span>
            </div>
          )}

          {/* Quick Info Bar (hidden while running/queued) -- 2 columns: Config | Variants */}
          {!showSpinner && (
            <div
              data-testid="quick-info-bar"
              className="grid grid-cols-[minmax(0,1fr)_72px] overflow-hidden border-t border-border-subtle bg-surface-0"
            >
              {/* Config Column */}
              <button
                data-testid="quick-info-config"
                title={configSummary}
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('config')
                }}
                className="group relative z-10 min-w-0 overflow-visible py-2.5 px-2 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag border-r border-border-subtle"
              >
                <span className="relative block max-w-full min-w-0">
                  <span
                    data-testid="quick-info-config-summary"
                    className="inline-block max-w-full min-w-0 truncate rounded-md border border-border-subtle bg-background/55 px-1.5 py-0.5 text-xxs text-muted-foreground leading-none backdrop-blur-md"
                  >
                    {configSummary}
                  </span>
                  <span
                    aria-hidden="true"
                    data-testid="quick-info-config-summary-expanded"
                    className="pointer-events-none absolute left-0 top-0 z-20 hidden w-max whitespace-nowrap rounded-md border border-border-subtle bg-background/85 px-1.5 py-0.5 text-xxs text-foreground shadow-lg backdrop-blur-md group-hover:inline-block group-focus-visible:inline-block"
                    style={{ maxWidth: nodeWidth - 16 }}
                  >
                    {configSummary}
                  </span>
                </span>
                {currentRefs.length > 0 ? (
                  <div className="flex items-center gap-1">
                    {currentRefs.slice(0, 2).map((ref, i) => {
                      const thumbUrl = projectId
                        ? getThumbnailUrl(projectId, ref.assetPath, 32, cacheBust)
                        : null
                      return thumbUrl ? (
                        <div key={i} className="w-6 h-6 rounded overflow-hidden border border-border">
                          <img src={thumbUrl} alt="" className="w-full h-full object-cover" />
                        </div>
                      ) : null
                    })}
                  </div>
                ) : (
                  <span className="text-xs text-text-muted/40">--</span>
                )}
              </button>

              {/* Variants Column */}
              <button
                data-testid="quick-info-variants"
                onClick={(e) => {
                  e.stopPropagation()
                  openPanelToTab('variants')
                }}
                className="min-w-0 overflow-hidden py-2.5 px-2 flex flex-col items-start gap-1 hover:bg-surface-1 transition-colors nodrag"
              >
                <span className="text-xxs text-muted-foreground leading-none">Variants</span>
                <div className="flex items-center gap-1.5">
                  <span
                    className="w-2 h-2 rounded-full"
                    style={{
                      backgroundColor:
                        historyEntries.length > 0 ? 'var(--primary)' : 'var(--text-muted)',
                    }}
                  />
                  <span className="text-xs font-medium text-foreground">
                    {historyEntries.length}
                  </span>
                </div>
              </button>
            </div>
          )}
        </div>

        {/* Unified Bottom Panel (both standalone and group mode) */}
        <UnifiedBottomPanel
          tabs={IMAGE_GEN_TABS}
          activeTab={activeTab}
          onTabChange={setActiveTab}
          isVisible={panelVisible}
          minWidth={500}
        >
          {(tab) => {
            if (tab === 'config' && projectId) {
              return (
                <ConfigTabContent
                  nodeId={id}
                  nodeType="imageGenerator"
                  prompt={in_prompt}
                  onPromptChange={(value) => updateConfig('in_prompt', value)}
                  references={currentRefs}
                  onReferencesChange={handleReferencesChange}
                  currentStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                  aspectRatio={in_aspect_ratio}
                  quality={in_quality}
                  model={in_model}
                  onAspectRatioChange={(ratio) => updateConfig('in_aspect_ratio', ratio)}
                  onQualityChange={(quality) => updateConfig('in_quality', quality)}
                  onModelChange={(model) => updateConfig('in_model', model)}
                  isRunning={isRunning || generateLocked}
                  onGenerate={handleGenerate}
                  onCancel={handleCancel}
                  isCancelling={isCancelling}
                />
              )
            }
            if (tab === 'refine') {
              return (
                <RefineTabContent
                  feedbackText={feedbackText}
                  onFeedbackTextChange={setFeedbackText}
                  onSubmit={handleRefineSubmit}
                  isRunning={isRunning}
                  nodeType="imageGenerator"
                  selectedStyleId={in_style}
                  onStyleChange={(styleId) => updateConfig('in_style', styleId)}
                />
              )
            }
            if (tab === 'variants') {
              return (
                <VariantsTabContent
                  history={historyEntries}
                  pickedIndex={pickedIndex}
                  onSelectVariant={handleHistorySelect}
                  batchPending={batchPending}
                  cacheKey={cacheBust}
                  projectId={projectId}
                  accentColor="purple"
                  aspectRatio={variantsGridAspect}
                />
              )
            }
            return null
          }}
        </UnifiedBottomPanel>
      </div>

      {/* Fullscreen lightbox */}
      {isModalOpen &&
        createPortal(
          <div
            className="fixed inset-0 z-[9999] bg-black/90 backdrop-blur-sm flex items-center justify-center p-8"
            onClick={() => setIsModalOpen(false)}
          >
            <div
              className="relative w-full max-w-6xl max-h-full flex items-center justify-center"
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsModalOpen(false)}
                className="absolute -top-12 right-0 text-text-secondary hover:text-foreground transition-colors"
              >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M6 18L18 6M6 6l12 12"
                  />
                </svg>
              </button>
              <img
                src={fullImageUrl ?? undefined}
                alt="Full size"
                className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
              />
            </div>
          </div>,
          document.body
        )}
    </>
  )
})

ImageGeneratorNode.displayName = 'ImageGeneratorNode'
export default ImageGeneratorNode
export { ASPECT_RATIO_OPTIONS }
